import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
// TEMP (DCM001CMG01-17401) — remove with the rest of the boot-trace instrumentation.
import { bootTrace, describeError } from 'app/shared/diagnostics/boot-trace';

// Only the request the boot hangs on, so the beacons stay readable.
const TRACED_URLS = /mylegalentities/;

interface DedupRule {
  test: RegExp;
  windowMs: number;
}

/**
 * Coalesces identical GET requests fired within a short time window: a duplicate
 * request (same method + full URL including query string) reuses the in-flight /
 * recently completed response instead of hitting the network again.
 *
 * This is NOT a debounce that delays the call — the FIRST request goes out
 * immediately; only redundant duplicates within the window are collapsed.
 *
 * Why: on the cold deep-link into the order flow (dashboard → new-order →
 * calendar + unify modal) the calendar bootstrap fires bursts of IDENTICAL
 * concurrent GETs — measured: myshipmentlocations x4 (same URL, same ms),
 * addresses x4+2, producttypes duplicates — from several components
 * cold-subscribing at once. These reads are idempotent and stable within
 * seconds, so collapsing the burst removes the duplicate network trips without
 * touching any store/effect semantics. Windows are tuned per endpoint;
 * session-stable data (jobsites, feature flags) gets a longer window.
 */
@Injectable()
export class ApiDedupInterceptor implements HttpInterceptor {
  private readonly cache = new Map<string, { obs: Observable<HttpEvent<unknown>>; at: number }>();

  // First matching rule wins; anything else uses defaultWindowMs.
  private readonly rules: DedupRule[] = [
    { test: /\/sm\/myshipmentlocations(\/|\?|$)/, windowMs: 3000 }, // jobsites: session-stable
    { test: /\/im\/features(\/|\?|$)/, windowMs: 3000 }, // feature flags: session-stable
    { test: /\/sm\/addresses(\/|\?|$)/, windowMs: 2500 }, // addresses don't change within seconds
    { test: /\/cgm\/producttypes(\/|\?|$)/, windowMs: 2500 },
    { test: /\/stm\/shippingconditions(\/|\?|$)/, windowMs: 2500 },
    { test: /\/cgm\/orders\/experiences(\/|\?|$)/, windowMs: 2000 },
  ];
  private readonly defaultWindowMs = 700;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only coalesce GETs (idempotent reads). POST/PUT/PATCH/DELETE pass through.
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    const key = req.urlWithParams;
    const rule = this.rules.find((r) => r.test.test(key));
    const windowMs = rule ? rule.windowMs : this.defaultWindowMs;

    const now = Date.now();
    const hit = this.cache.get(key);
    if (hit && now - hit.at < windowMs) {
      // TEMP (DCM001CMG01-17401): the boot hangs waiting on mylegalentities, and
      // this interceptor is the outermost one — it hands the SAME shared
      // observable to every caller inside the window, so it is the prime suspect
      // for a subscriber that never sees the response. Trace it for the boot
      // request only, to keep the beacon volume low.
      if (TRACED_URLS.test(key)) {
        bootTrace('dedup:hit', `age=${now - hit.at}ms window=${windowMs}ms`);
      }
      // Reuse the identical recent / in-flight request's response.
      return hit.obs;
    }
    if (TRACED_URLS.test(key)) {
      bootTrace('dedup:miss', `window=${windowMs}ms`);
    }

    const traced = TRACED_URLS.test(key);
    const shared = next
      .handle(req)
      .pipe(
        traced
          ? tap({
              next: (event) => bootTrace('dedup:event', `type=${(event as { type?: number }).type}`),
              complete: () => bootTrace('dedup:complete'),
              error: (error) => bootTrace('dedup:error', describeError(error)),
            })
          : tap(),
      )
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.cache.set(key, { obs: shared, at: now });

    // Evict once the window elapses so later (legitimate) calls fetch fresh data.
    setTimeout(() => {
      const current = this.cache.get(key);
      if (current && current.obs === shared) {
        this.cache.delete(key);
      }
    }, windowMs);

    return shared;
  }
}
